<template>
  <ODialog
    ref="dialog"
    :title="$t('resource.remove')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Service } from '~/_api/models/service'

interface ServiceResource {
  name?: string
  idServiceResource?: number
}

interface Props {
  service: Service
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false
})

const emit = defineEmits<Emits>()
const { $i18n, $notification, $api } = useNuxtApp()

const dialog = ref()
const serviceResource = ref<ServiceResource | null>(null)

const info = computed(() => {
  return $i18n.t('components.service.actions.removeResource.info', { 
    name: serviceResource.value?.name, 
    id: serviceResource.value?.idServiceResource 
  })
})

const open = (resource: ServiceResource) => {
  serviceResource.value = resource
  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.removeResource(props.service, serviceResource.value)
    const service = response.data
    emit('update:loading', false)

    if (service) {
      $notification.toast({
        title: $i18n.t('components.service.actions.removeResource.success'),
        text: $i18n.t('components.service.actions.removeResource.successInfo', { name: serviceResource.value?.name }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    emit('error')
  }
}

defineExpose({
  open
})
</script>
